import { LOAD_NUMBER } from 'config'
import { pathApiAnonymousCalls } from 'lib/paths'
import cookie from 'lib/cookie'
import {
  CallActionSignature,
  CallBGM,
  ConferenceCall,
  ConferenceCallUserRole,
  Post,
  User,
} from 'lib/_types'

import { isCallUserAnonymous } from 'features/ConferenceCall/helpers'
import { selectIsLoggedIn } from '../../selectors'
import { yayApi } from '../baseApi'

export const CallTags = {
  Root: 'Call',
  Users: 'Call/Users',
  BGMList: 'Call/BGMList',
  AnonymousCalls: 'AnonymousCalls',
}

export const callApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      CallTags.Root,
      CallTags.BGMList,
      CallTags.Users,
      CallTags.AnonymousCalls,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getCallInvitableList: build.query({
        query: ({ number = LOAD_NUMBER, callId, fromTimestamp }) => ({
          url: `/v1/calls/${callId}/users/invitable`,
          params: {
            number,
            from_timestamp: fromTimestamp,
          },
        }),
      }),

      // Conference Call
      getCallDetails: build.query<ConferenceCall, { conferenceId: string }>({
        providesTags: [CallTags.Root],
        async queryFn({ conferenceId }, api, _extraOptions, baseQuery) {
          const isAnonymousCall = !selectIsLoggedIn(api.getState())

          const res = await baseQuery({
            url: isAnonymousCall
              ? `/v1/anonymous_calls/${conferenceId}?agora_uid=${cookie.chatUuid.get()}`
              : `/v2/calls/conferences/${conferenceId}`,
          })

          const users = {}
          const conferenceCall: ConferenceCall = isAnonymousCall
            ? res?.data?.post?.conference_call
            : res?.data?.conference_call

          conferenceCall?.conference_call_users?.forEach((callUser) => {
            const callUserId = callUser.id.toString()

            users[callUserId] = {
              ...callUser,
              id: callUserId,
            }
          })

          conferenceCall?.conference_call_user_roles?.forEach(
            ({ user_id, role }) => {
              const callUserId = user_id.toString()

              if (users[callUserId]) {
                users[callUserId] = {
                  ...users[callUserId],
                  role,
                }
              }
            }
          )

          return {
            data: {
              ...conferenceCall,
              users,
              host_id: conferenceCall?.conference_call_user_roles?.find(
                (u) => u.role === 'host'
              )?.user_id,
            },
          }
        },
      }),

      updateCall: build.mutation<
        ConferenceCall,
        {
          id: string
          mode?: string
          gameId?: string
          gameTitle?: string
          categoryId?: string
        }
      >({
        invalidatesTags: [CallTags.Root],
        query: ({ id, mode, gameId, gameTitle, categoryId }) => ({
          url: `/v1/calls/${id}`,
          method: 'PUT',
          data: {
            joinable_by: mode,
            game_id: gameId,
            game_title: gameTitle,
            category_id: categoryId,
          },
        }),
      }),

      startCall: build.mutation<
        {
          conference_call: ConferenceCall
          conference_call_user_roles: ConferenceCallUserRole[]
          conference_call_user_uuid: string
        },
        {
          conferenceId: string
          chatUuid?: string
        }
      >({
        invalidatesTags: [CallTags.Root],
        query: ({ conferenceId, chatUuid }) => {
          const isAnonymousCall = isCallUserAnonymous(chatUuid)

          return {
            url: isAnonymousCall
              ? '/v1/anonymous_calls/start_conference_call'
              : '/v2/calls/start_conference_call',
            method: 'POST',
            data: {
              conference_id: conferenceId,
              agora_uid: isAnonymousCall ? chatUuid : undefined,
            },
          }
        },
        transformErrorResponse: (res) => {
          res.data.variant = 'alert'

          return res
        },
      }),

      leaveCall: build.mutation<
        any,
        {
          conferenceId: string
          chatUuid: string
          duration: number
          totalUsersInCall: number
        }
      >({
        invalidatesTags: [CallTags.Root],
        query: ({ conferenceId, chatUuid, duration, totalUsersInCall }) => {
          const isAnonymousCall = isCallUserAnonymous(chatUuid)

          return {
            url: isAnonymousCall
              ? '/v1/anonymous_calls/leave_conference_call'
              : '/v1/calls/leave_conference_call',
            method: 'POST',
            data: {
              conference_id: conferenceId,
              call_sid: '',
              agora_uid: isAnonymousCall ? chatUuid : undefined,
              duration,
              total_users_in_call: totalUsersInCall,
            },
          }
        },
      }),

      updateCallUserRole: build.mutation<
        any,
        {
          conferenceId: string
          userId: string
          role: string
        }
      >({
        invalidatesTags: [CallTags.Root],
        query: ({ conferenceId, userId, role }) => ({
          url: `/v1/calls/${conferenceId}/users/${userId}`,
          method: 'PUT',
          data: {
            role, // 'participant' | 'moderator'
          },
        }),
      }),

      kickCallUser: build.mutation<
        {
          result: string
          signature_payload: CallActionSignature
        },
        {
          conferenceId: string
          uuid: string
          ban: boolean
        }
      >({
        query: ({ conferenceId, uuid, ban }) => ({
          url: `/v3/calls/conference_calls/${conferenceId}/kick`,
          method: 'POST',
          data: {
            uuid,
            ban,
          },
        }),
      }),

      generateSignatureCallUser: build.mutation<
        {
          result: string
          signature_payload: CallActionSignature
        },
        {
          conferenceId: number
          targetUserUuid: string
          action: string
        }
      >({
        query: ({ conferenceId, targetUserUuid, action }) => ({
          url: '/v1/calls/action_signature/generate',
          method: 'POST',
          data: {
            conference_id: conferenceId,
            target_user_uuid: targetUserUuid,
            action,
          },
        }),
      }),

      validateSignatureCallUser: build.mutation<
        {
          result: string
        },
        {
          conferenceId: number
          senderUuid: string
          receiverUuid: string
          action: string
          timestamp: string
          signature: string
        }
      >({
        query: ({
          conferenceId,
          senderUuid,
          receiverUuid,
          action,
          timestamp,
          signature,
        }) => ({
          url: '/v2/calls/action_signature/validate',
          params: {
            call_id: conferenceId,
            sender_uuid: senderUuid,
            receiver_uuid: receiverUuid,
            action,
            timestamp,
            signature,
          },
        }),
      }),

      leaveAgoraChannel: build.mutation<
        any,
        {
          conferenceId: string
          userId: string
          isAnonymous: boolean
        }
      >({
        query: ({ conferenceId, userId, isAnonymous }) => ({
          url: isAnonymous
            ? '/v1/anonymous_calls/leave_agora_channel'
            : '/v1/calls/leave_agora_channel',
          method: 'POST',
          data: isAnonymous
            ? {
                conference_id: conferenceId,
                agora_uid: userId,
              }
            : {
                conference_id: conferenceId,
                user_id: userId,
              },
        }),
      }),

      inviteCallVoip: build.mutation<
        any,
        {
          chatRoomId: string
          conferenceRoomId: string
        }
      >({
        query: ({ chatRoomId, conferenceRoomId }) => ({
          url: `/v2/calls/invite`,
          method: 'POST',
          data: {
            chat_room_id: chatRoomId,
            room_id: conferenceRoomId,
            room_url: '',
          },
        }),
      }),

      inviteUserToCall: build.mutation<
        any,
        {
          userId: string
          conferenceId: string
        }
      >({
        query: ({ userId, conferenceId }) => {
          const formData = new FormData()
          formData.append('user_ids[]', userId)

          return {
            url: `/v1/calls/conference_calls/${conferenceId}/invite`,
            method: 'POST',
            data: formData,
            config: {
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          }
        },
      }),

      updateCallOrder: build.mutation<any, { conferenceId: string }>({
        query: ({ conferenceId }) => ({
          url: `/v1/calls/${conferenceId}/bump`,
          method: 'POST',
          data: {
            participant_limit: 5, // https://jira.nanameue.jp/browse/YAY-4716
          },
        }),
      }),

      // RTM
      getRTMToken: build.mutation<
        { token: string; agora_uid: string },
        { conferenceId: string | number; isAnonymous?: boolean }
      >({
        query: ({ conferenceId, isAnonymous }) => ({
          url: isAnonymous
            ? `/v2/anonymous_calls/agora_rtm_token?conference_id=${conferenceId}`
            : `/v2/calls/${conferenceId}/agora_rtm_token`,
          method: 'GET',
        }),
      }),

      // BGM
      getBGMList: build.query<Array<CallBGM>, { isAnonymous?: boolean }>({
        providesTags: [CallTags.BGMList],
        query: ({ isAnonymous }) => ({
          url: isAnonymous ? '/v1/anonymous_calls/bgm' : '/v1/calls/bgm',
        }),
        transformResponse: (res) => res?.bgm,
      }),

      getAnonymousCalls: build.query<{ posts: Array<Post> }, void>({
        providesTags: [CallTags.AnonymousCalls],
        query: () => ({
          url: pathApiAnonymousCalls,
        }),
      }),

      getCallParticipantsInfo: build.mutation<
        User,
        {
          conferenceId: string | number
          uuid: string | number
          chatUuid?: string
        }
      >({
        query: ({ conferenceId, uuid, chatUuid }) => ({
          url: isCallUserAnonymous(chatUuid)
            ? `v1/anonymous_calls/${conferenceId}/participants/${uuid}?agora_uid=${chatUuid}`
            : `v1/calls/${conferenceId}/participants/${uuid}`,
        }),
      }),
    }),
    overrideExisting: false,
  })

export const {
  useGetCallInvitableListQuery,
  useGetCallDetailsQuery,
  useLeaveCallMutation,
  useStartCallMutation,
  useInviteCallVoipMutation,
  useUpdateCallOrderMutation,
  useGetRTMTokenMutation,
  useGetBGMListQuery,
  useKickCallUserMutation,
  useGenerateSignatureCallUserMutation,
  useValidateSignatureCallUserMutation,
  useUpdateCallMutation,
  useInviteUserToCallMutation,
  useLeaveAgoraChannelMutation,
  useUpdateCallUserRoleMutation,
  useGetAnonymousCallsQuery,
  useGetCallParticipantsInfoMutation,
} = callApi
